// lib/klassenEinfach.js — Startklassen in einfachen Worten (25.10.2026, Stufe 2)
//
// Befund im Muster Vereinsanmeldung: ein 12-jähriges Anfänger-Mädchen bekam
// 64 Klassen angeboten, englisch („Weapons, Bladed“), mit Codes wie BWP-227w
// als Hauptinformation, und Anfängern standen auch Intermediate/Advanced
// offen. Am Handy war die Liste gar nicht zu sehen.
//
// Hier steht, was die geführte Anmeldung (components/anmeldung/AnmeldeAblauf)
// daraus macht:
//   1. Disziplin in einfachen Worten mit kurzer Erklärung (DISZIPLINEN).
//   2. Je Disziplin nur die wirklich passenden Klassen (einfacheKlassen):
//      Alter am Stichtag IN der Klasse (keine „eine höher“), Geschlecht,
//      Gewicht IN der Klasse, Erfahrung GENAU die eigene Stufe. Gleich
//      benannte Klassen schrumpfen auf die engste Altersspanne.
//   3. Deutscher Name vorne, Code klein hinten (deutscherName).
//
// ⚠️ Das ist eine Teilmenge der Regel in lib/divisionsFilter.js (passende-
// Divisionen, gleiche Regel wie der Server in utils/kategoriePruefung.js).
// Was hier fehlt, bleibt über „Alle Klassen anzeigen“ erreichbar. Was der
// Server ablehnt, steht hier nie.
import {
  passendeDivisionen, alterAmStichtag, alterPasst, gewichtPasst, geschlechtPasst,
  skillPasst, istFormenTyp, istKampfTyp, SKILL_STUFEN, GURTFARBE_SKILL,
} from './divisionsFilter';
import { anzeigeName } from './kategorieText';

const typ = (d) => String(d?.Division_Type || '').trim();
const name = (d) => String(d?.Division_Name || '').trim();

/** Duo- oder Team-Klasse? Gleiche Regel wie backend/utils/startgebuehr.js istTeamDivision. */
export function teamArt(d) {
  const n = name(d).toLowerCase();
  if (n.includes('duo')) return 'duo';
  const flagge = String(d?.Team ?? '').trim().toLowerCase();
  if (flagge === 'team' || flagge === '1' || flagge === 'true' || n.includes('team')) return 'team';
  return null;
}

const istWaffe = (d) => /weapon/i.test(name(d));

// Reihenfolge = Reihenfolge der Kacheln
export const DISZIPLINEN = [
  { id: 'formen', name: 'Formen', text: 'Eine Form (Kata) ohne Waffe vorführen.',
    passt: (d) => typ(d) === 'Formen' && !istWaffe(d) && !teamArt(d) },
  { id: 'waffen', name: 'Formen mit Waffe', text: 'Eine Form mit Bo, Sai, Schwert oder einer anderen Waffe vorführen.',
    passt: (d) => typ(d) === 'Formen' && istWaffe(d) && !teamArt(d) },
  { id: 'formen_team', name: 'Formen zu zweit oder im Team', text: 'Zusammen mit Partnern aus deinem Verein eine Form zeigen.',
    passt: (d) => typ(d) === 'Formen' && !!teamArt(d) },
  { id: 'sv', name: 'Selbstverteidigung', text: 'Abwehr eines Angriffs vorführen, mit Partner oder im Team.',
    passt: (d) => typ(d) === 'Selbstverteidigung' },
  { id: 'punkte', name: 'Leichtkontakt mit Punkten', text: 'Kampf, der nach jedem Treffer unterbrochen wird (Pointfighting).',
    passt: (d) => typ(d) === 'Pointfighting' },
  { id: 'durchgehend', name: 'Leichtkontakt durchgehend', text: 'Kampf läuft weiter, Treffer werden mitgezählt (Continuous Fighting).',
    passt: (d) => typ(d) === 'Continous Fighting' || typ(d) === 'Continuous Fighting' },
  { id: 'k1', name: 'Kickboxen K1', text: 'Kickboxen nach K1-Regeln.',
    passt: (d) => typ(d) === 'K1' },
  { id: 'voll', name: 'Vollkontakt', text: 'Kickboxen mit vollem Kontakt (Fullcontact).',
    passt: (d) => typ(d) === 'Fullcontact' },
  { id: 'kumite', name: 'Kumite', text: 'Karate-Kampf.',
    passt: (d) => typ(d) === 'Kumite' },
  { id: 'boden', name: 'Bodenkampf', text: 'Ringen am Boden, ohne Schläge (Grappling).',
    passt: (d) => typ(d) === 'Grappling' },
  { id: 'rumble', name: 'Rumble', text: 'Wettkampf für Kinder bis 12 Jahre.',
    passt: (d) => typ(d) === 'Rumble' },
  { id: 'bruch', name: 'Bruchtest', text: 'Bretter oder anderes Material mit einer Technik brechen.',
    passt: (d) => typ(d) === 'Bruchtest' },
];
const SONST = { id: 'sonst', name: 'Weitere Klassen', text: 'Klassen, die keiner Disziplin oben zugeordnet sind.', passt: () => true };

export function disziplinVon(d) {
  return (DISZIPLINEN.find((x) => x.passt(d)) || SONST).id;
}
export function disziplinInfo(id) {
  return DISZIPLINEN.find((x) => x.id === id) || SONST;
}

// Deutsche Namen. Nur Anzeige: gespeichert und verglichen wird der Code.
const NAMEN = [
  [/^(forms, )?japanese,\s*hardstyle$/i, 'Formen japanisch (Kata)'],
  [/^(forms, )?korean,?\s*hardstyle$/i, 'Formen koreanisch'],
  [/^forms, kenpo\/kempo\/chinese$/i, 'Formen Kenpo / chinesisch'],
  [/^forms, chinese,\s*softstyle$/i, 'Formen chinesisch, weicher Stil'],
  [/^forms, open\/(creative\/music|music\/creative)$/i, 'Offene Formen (kreativ, mit Musik)'],
  [/^weapons$/i, 'Waffenform'],
  [/^weapons, bladed$/i, 'Waffenform mit Klinge'],
  [/^weapons, wooden$/i, 'Waffenform mit Holzwaffe'],
  [/^weapons, traditional bladed$/i, 'Traditionelle Waffenform mit Klinge'],
  [/^weapons, traditional wooden$/i, 'Traditionelle Waffenform mit Holzwaffe'],
  [/^(weapon, open\/creative\/music|musical forms weapons)$/i, 'Offene Waffenform (kreativ, mit Musik)'],
  [/^chinese weapons$/i, 'Chinesische Waffenform'],
  [/^duo forms non weapons$/i, 'Formen zu zweit, ohne Waffe'],
  [/^duo forms weapons$/i, 'Formen zu zweit, mit Waffe'],
  [/^teamform non weapons$/i, 'Formen im Team, ohne Waffe'],
  [/^teamform (all kind of )?weapons$/i, 'Formen im Team, mit Waffe'],
  [/^self defense 1\/1$/i, 'Selbstverteidigung mit Partner'],
  [/^real self defense$/i, 'Selbstverteidigung realitätsnah'],
  [/^team self defense$/i, 'Selbstverteidigung im Team'],
  [/^point sparring$/i, 'Leichtkontakt mit Punkten'],
  [/^contin(u)?ous fighting$/i, 'Leichtkontakt durchgehend'],
  [/^k1 rules$/i, 'Kickboxen K1'],
  [/^fullcontact$/i, 'Vollkontakt'],
  [/^kumite$/i, 'Kumite'],
  [/^wood breaking$/i, 'Bruchtest Holz'],
  [/^creative breaking$/i, 'Bruchtest kreativ'],
  [/^breaking$/i, 'Bruchtest Endrunde'],
];

export function deutscherName(d) {
  const n = name(d);
  if (typ(d) === 'Grappling') return 'Bodenkampf';
  if (typ(d) === 'Rumble') return 'Rumble';
  for (const [muster, text] of NAMEN) if (muster.test(n)) return text;
  return anzeigeName(n) || String(d?.Division_Code || '');
}

// ── Erfahrung ───────────────────────────────────────────────────────────────
// Eigene Stufe → genau diese Stufe der Klasse (ohne „eine höher“ bei Formen).
const GENAU = {
  beginner: ['Beginner', 'Rookie Grappling'],
  intermediate: ['Intermediate', 'Advanced Grappling'],
  advanced: ['Advanced', 'Master Grappling'],
  'black belt': ['Black Belts', 'Master Grappling'],
};

// ⚠️ Bodenkampf der Kinder (8–13, 14–16) trägt KEINE Stufe in Skill_Level,
// nur im Code: GRR…, GRA…, GRM… Gemessen in tda_r4 am 25.10.2026 an den
// Klassen MIT Stufe: GRR = 11× Rookie Grappling, GRA = 6× Advanced
// Grappling (+1× Rookie), GRM = 6× Master Grappling. Ohne diese Ableitung
// bekäme ein Anfänger alle drei Stufen zur Wahl.
function stufeAusCode(d) {
  const c = String(d?.Division_Code || '').toUpperCase();
  if (typ(d) !== 'Grappling') return null;
  if (c.startsWith('GRR')) return 'Rookie Grappling';
  if (c.startsWith('GRA')) return 'Advanced Grappling';
  if (c.startsWith('GRM')) return 'Master Grappling';
  return null;
}

export function stufeDerKlasse(d) {
  const s = String(d?.Skill_Level || '').trim();
  if (SKILL_STUFEN.has(s)) return s;
  return stufeAusCode(d);
}

/** Eigenes Können als Schlüssel (beginner|intermediate|advanced|black belt) */
export function koennenVon(p, d) {
  const formen = istFormenTyp(typ(d));
  const kampf = istKampfTyp(typ(d));
  const roh = formen ? (p.skill_level_kata || p.skill_level) : (kampf ? (p.skill_level_grappling || p.skill_level) : p.skill_level);
  const k = String(roh || '').toLowerCase();
  if (GENAU[k]) return k;
  return GURTFARBE_SKILL[String(p.gurtfarbe || '').trim().toLowerCase()] || null;
}

const STUFE_TEXT = {
  'Beginner': 'Anfänger', 'Rookie Grappling': 'Anfänger',
  'Intermediate': 'Fortgeschritten', 'Advanced Grappling': 'Fortgeschritten',
  'Advanced': 'Erfahren', 'Master Grappling': 'Erfahren',
  'Black Belts': 'Schwarzgurt', 'Grand Champion': 'Endrunde',
};
export const stufeText = (s) => STUFE_TEXT[s] || null;

function erfahrungGenau(d, p) {
  const s = stufeDerKlasse(d);
  if (!s) return true;                     // keine Stufe: offen für alle
  if (s === 'Grand Champion') return false; // Endrunde nur über „Alle Klassen“
  const k = koennenVon(p, d);
  if (!k) return true;                     // kein Können gepflegt: nicht aussperren
  return GENAU[k].includes(s);
}

const zahl = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : null; };
function spanne(d) {
  const von = zahl(d.Age_from), bis = zahl(d.Age_to);
  return von === null || bis === null ? 999 : bis - von;
}

/** Bestandteile für „Mädchen · 9–13 Jahre · Anfänger · 38–42 kg“ */
function klassenTeile(d) {
  const g = String(d?.Gender || '').trim().toLowerCase();
  const kinder = zahl(d.Age_to) !== null && zahl(d.Age_to) <= 17;
  const geschlecht = g.includes('/') || g === 'mixed' || !g ? 'gemischt'
    : g.startsWith('f') ? (kinder ? 'Mädchen' : 'Frauen') : (kinder ? 'Jungen' : 'Männer');
  const von = zahl(d.Age_from), bis = zahl(d.Age_to);
  let alter = '';
  if (von !== null && bis !== null) {
    if (von <= 0 && bis >= 99) alter = 'jedes Alter';
    else if (von <= 0) alter = `bis ${bis} Jahre`;
    else if (bis >= 99) alter = `ab ${von} Jahren`;
    else alter = `${von}–${bis} Jahre`;
  }
  const min = zahl(d.Minimun_Weight), max = zahl(d.Maximum_Weight);
  let gewicht = '';
  if (min !== null && max !== null && !(min <= 0 && max >= 999)) {
    gewicht = min <= 0 ? `bis ${max} kg` : max >= 150 ? `ab ${min} kg` : `${min}–${max} kg`;
  }
  const art = teamArt(d);
  return { geschlecht, alter, stufe: stufeText(stufeDerKlasse(d)), gewicht, art: art === 'duo' ? 'zu zweit' : art === 'team' ? 'im Team' : '' };
}

/** „Mädchen · 9–13 Jahre · Anfänger · 38–42 kg“ */
export function klassenText(d) {
  const t = klassenTeile(d);
  return [t.geschlecht, t.alter, t.stufe, t.gewicht, t.art].filter(Boolean).join(' · ');
}

/**
 * Passende Klassen je Disziplin.
 *
 * @param {object[]} alle      freigegebene Klassen des Turniers (/api/divisionen?nurFreigegeben=1)
 * @param {object}   p         Sportler (geburtsdatum, geschlecht, gewicht, skill_level, gurtfarbe)
 * @param {string}   stichtag  'JJJJ-MM-TT' aus der Kategorienliste
 * @returns {{ passend: object[], einfach: Record<string, object[]>, alle: Record<string, object[]> }}
 *   passend = was der Server annimmt; einfach/alle je Disziplin
 */
export function einfacheKlassen(alle, p, stichtag) {
  const passend = passendeDivisionen(alle, p, stichtag);
  const alter = alterAmStichtag(p.geburtsdatum, stichtag);
  const gewicht = parseFloat(p.gewicht) || 0;
  const eng = passend.filter((d) =>
    (alter === null || alterPasst(d, alter, alle, false))
    && gewichtPasst(d, gewicht, alle, false)
    && erfahrungGenau(d, p));

  const jeDisziplin = (liste) => {
    const m = {};
    for (const d of liste) (m[disziplinVon(d)] = m[disziplinVon(d)] || []).push(d);
    return m;
  };

  // Gleich benannte Klassen (z. B. Duo-Formen 0–17 und 0–99): nur die engste
  // Altersspanne bleibt. Ein 12-Jähriger soll nicht zwischen „bis 17“ und
  // „jedes Alter“ raten.
  const einfach = {};
  for (const [id, liste] of Object.entries(jeDisziplin(eng))) {
    const gruppen = new Map();
    for (const d of liste) {
      const t = klassenTeile(d);
      const k = [deutscherName(d), t.geschlecht, t.stufe, t.gewicht, t.art].join('|');
      if (!gruppen.has(k)) gruppen.set(k, []);
      gruppen.get(k).push(d);
    }
    // ⚠️ Bleiben danach mehrere gleich lautende Klassen (Datenbank: DTMUBF,
    // DTMUBWP und DTUBWP heißen alle „Duo Forms Weapons“), steht hier nur die
    // erste nach Code; die anderen zeigt „Alle Klassen anzeigen“. Drei gleiche
    // Karten mit verschiedenen Codes lassen den Verein nur raten.
    einfach[id] = [...gruppen.values()].map((g) => {
      const min = Math.min(...g.map(spanne));
      return g.filter((d) => spanne(d) === min)
        .sort((x, y) => String(x.Division_Code).localeCompare(String(y.Division_Code), 'de'))[0];
    });
  }
  return { passend, einfach, alle: jeDisziplin(passend) };
}

/**
 * Warum stehen nicht alle Klassen da? Zählt je ersten Grund.
 * @returns {{ gesamt:number, alter:number, geschlecht:number, erfahrung:number, gewicht:number, aehnlich:number }}
 */
export function warumNichtAlle(alle, p, stichtag, angezeigt) {
  const alter = alterAmStichtag(p.geburtsdatum, stichtag);
  const gewicht = parseFloat(p.gewicht) || 0;
  const z = { gesamt: (alle || []).length, alter: 0, geschlecht: 0, erfahrung: 0, gewicht: 0, aehnlich: 0 };
  for (const d of alle || []) {
    const kampf = istKampfTyp(typ(d));
    const formen = istFormenTyp(typ(d));
    if (alter !== null && !alterPasst(d, alter, alle, kampf)) z.alter += 1;
    else if (!geschlechtPasst(d, p.geschlecht)) z.geschlecht += 1;
    else if (!skillPasst(d.Skill_Level, String(koennenVon(p, d) || ''), formen)) z.erfahrung += 1;
    else if (!gewichtPasst(d, gewicht, alle, kampf)) z.gewicht += 1;
  }
  z.aehnlich = Math.max(0, z.gesamt - z.alter - z.geschlecht - z.erfahrung - z.gewicht - angezeigt);
  return z;
}
